import React, { useState } from 'react';
import {
  Box, Paper, Typography, Select, MenuItem, InputBase, Button, Breadcrumbs, Link as MuiLink,
} from '@mui/material';
import { SearchRounded, NavigateNextRounded } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { colors } from '../theme/theme';

/** Card with a header row (title + optional right-side action) and body. */
export const SectionCard: React.FC<{
  title?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode; noPadding?: boolean; sx?: any;
}> = ({ title, action, children, noPadding, sx }) => (
  <Paper sx={{ p: noPadding ? 0 : 2.5, height: '100%', ...sx }}>
    {title && (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: noPadding ? 0 : 2, ...(noPadding ? { px: 2.5, pt: 2.25 } : {}) }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{title}</Typography>
        {action}
      </Box>
    )}
    {children}
  </Paper>
);

/** Responsive CSS grid. `cols` = column count on desktop. */
export const Grid: React.FC<{ cols: number; gap?: number; children: React.ReactNode; sx?: any }> = ({ cols, gap = 2, children, sx }) => (
  <Box
    sx={{
      display: 'grid', gap, alignItems: 'stretch',
      gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(2, cols)}, 1fr)`, md: `repeat(${cols}, 1fr)` },
      ...sx,
    }}
  >
    {children}
  </Box>
);

/** Filter bar container (white card). */
export const FilterBar: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Paper sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap', p: 2, mb: 2.5 }}>
    {children}
  </Paper>
);

export const Field: React.FC<{ label: string; children: React.ReactNode; minWidth?: number }> = ({ label, children, minWidth = 150 }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth }}>
    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: colors.textSecondary }}>{label}</Typography>
    {children}
  </Box>
);

export const Spacer = () => <Box sx={{ flex: 1 }} />;

/** Compact search input used inside filter bars. */
export const SearchInput: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string; width?: number }> = ({
  value, onChange, placeholder = 'Search…', width = 240,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: `1px solid ${colors.border}`, borderRadius: '10px', px: 1.5, py: 0.75, width, maxWidth: '100%' }}>
    <SearchRounded sx={{ fontSize: 18, color: colors.textSecondary }} />
    <InputBase value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} sx={{ fontSize: 14, flex: 1 }} />
  </Box>
);

/** Select bound to a string value (used for filters). `options` may be strings or {value,label}. */
export const SelectInput: React.FC<{
  value: string; onChange: (v: string) => void; options: (string | { value: string; label: string })[]; minWidth?: number;
}> = ({ value, onChange, options, minWidth = 150 }) => (
  <Select value={value} onChange={(e) => onChange(e.target.value)} displayEmpty size="small" sx={{ minWidth, bgcolor: '#fff' }}>
    {options.map((o) => {
      const val = typeof o === 'string' ? o : o.value;
      const lbl = typeof o === 'string' ? o : o.label;
      return <MenuItem key={val} value={val}>{lbl}</MenuItem>;
    })}
  </Select>
);

/** Page breadcrumb (e.g. Businesses › Acme). */
export const Crumbs: React.FC<{ items: { label: string; to?: string }[] }> = ({ items }) => (
  <Breadcrumbs separator={<NavigateNextRounded sx={{ fontSize: 16 }} />} sx={{ mb: 1.75, fontSize: 13 }}>
    {items.map((it, i) =>
      it.to ? (
        <MuiLink key={i} component={RouterLink} to={it.to} underline="hover" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
          {it.label}
        </MuiLink>
      ) : (
        <Typography key={i} sx={{ color: colors.textTertiary, fontSize: 13 }}>{it.label}</Typography>
      )
    )}
  </Breadcrumbs>
);

/** Detail page header: title + right-aligned actions. */
export const DetailHead: React.FC<{ title: React.ReactNode; sub?: React.ReactNode; actions?: React.ReactNode }> = ({ title, sub, actions }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
    <Box>
      <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{title}</Typography>
      {sub && <Typography sx={{ color: colors.textSecondary, fontSize: 13.5, mt: 0.5 }}>{sub}</Typography>}
    </Box>
    {actions && <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>{actions}</Box>}
  </Box>
);

/** Key/value row used in hero/detail panels. */
export const KV: React.FC<{ k: React.ReactNode; v: React.ReactNode; dashed?: boolean }> = ({ k, v, dashed }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, py: 0.9, fontSize: 13, borderBottom: dashed ? `1px dashed ${colors.border}` : 'none' }}>
    <Box component="span" sx={{ color: colors.textSecondary }}>{k}</Box>
    <Box component="span" sx={{ fontWeight: 600, textAlign: 'right' }}>{v}</Box>
  </Box>
);

/** Grey rounded panel grouping a set of KV rows. */
export const StatBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ bgcolor: colors.gray50, borderRadius: '12px', px: 1.75, py: 0.75 }}>{children}</Box>
);

/** Simple tab bar driven by a controlled active key. */
export const TabBar: React.FC<{ tabs: { key: string; label: string }[]; active: string; onChange: (k: string) => void }> = ({ tabs, active, onChange }) => (
  <Box sx={{ display: 'flex', gap: 0.5, borderBottom: `1px solid ${colors.border}`, mb: 2.5, flexWrap: 'wrap' }}>
    {tabs.map((t) => (
      <Box
        key={t.key}
        onClick={() => onChange(t.key)}
        sx={{
          px: 1.75, py: 1.4, fontSize: 14, fontWeight: 600, cursor: 'pointer', mb: '-1px',
          color: active === t.key ? colors.primary : colors.textSecondary,
          borderBottom: `2px solid ${active === t.key ? colors.primary : 'transparent'}`,
        }}
      >
        {t.label}
      </Box>
    ))}
  </Box>
);

/** Page intro paragraph. */
export const PageSub: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ color: colors.textSecondary, fontSize: 13.5, mb: 2.5 }}>{children}</Typography>
);

/** Reset button used in filter bars. */
export const ResetButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Button variant="outlined" onClick={onClick} sx={{ borderColor: colors.border, color: colors.textPrimary, height: 40, '&:hover': { borderColor: colors.border, bgcolor: colors.gray50 } }}>
    Reset
  </Button>
);

/** Hook for a controlled tab. */
export function useTab(initial: string) {
  return useState(initial);
}
